import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Space,
  message, Popconfirm, Card, Row, Col,
  Pagination, Alert, Tag, Badge, Divider, Tooltip
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined,
  SearchOutlined, EyeOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';

const { Option } = Select;
const { TextArea } = Input;

const RoleManagementPage = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
 
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRoles, setTotalRoles] = useState(0);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Fetch roles
  const fetchRoles = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/roles', {
        params: {
          page,
          limit: size,
          search: searchText
        }
      });
     
      if (response.data.success) {
        const rolesData = response.data.roles || [];
        const formattedRoles = rolesData.map(role => ({
          ...role,
          formattedCreatedAt: formatDate(role.createdAt),
          formattedUpdatedAt: formatDate(role.updatedAt)
        }));
        setRoles(formattedRoles);
        setTotalRoles(response.data.total || rolesData.length);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        message.error(response.data.message || 'Error loading roles');
        setRoles([]);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Error loading roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/api/permissions');
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  useEffect(() => {
    fetchRoles(currentPage, pageSize);
    fetchPermissions();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setErrorMessage('');
   
    try {
      if (editingRole) {
        values.id = editingRole._id;
        const response = await axios.put(`/api/roles/${editingRole._id}`, values);
       
        if (response.data.success) {
          message.success('Role updated successfully');
          setModalVisible(false);
          setEditingRole(null);
          form.resetFields();
          fetchRoles(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Error updating role');
        }
      } else {
        const response = await axios.post('/api/roles', values);
       
        if (response.data.success) {
          message.success('Role added successfully');
          setModalVisible(false);
          form.resetFields();
          fetchRoles(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Error creating role');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.response?.data?.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (role) => {
    try {
      const response = await axios.delete(`/api/roles/${role._id}`);
      if (response.data.success) {
        message.success('Role deleted successfully');
        fetchRoles(currentPage, pageSize);
      } else {
        message.error(response.data.message || 'Error deleting role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      message.error(error.response?.data?.message || 'Error deleting role');
    }
  };

  const handleSearch = () => {
    fetchRoles(1, pageSize);
  };

  const handlePageChange = (page, size) => {
    fetchRoles(page, size);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: 'id',
      width: 50,
      render: (text, record, index) => {
        const pageIndex = (currentPage - 1) * pageSize + index + 1;
        return pageIndex;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions) => (
        <div>
          {permissions && permissions.length > 0 ? (
            <>
              {permissions.slice(0, 2).map((perm, index) => (
                <Tag key={index} color="blue" style={{ margin: '2px' }}>
                  {perm}
                </Tag>
              ))}
              {permissions.length > 2 && (
                <Tooltip title={permissions.slice(2).join(', ')}>
                  <Tag>+{permissions.length - 2}</Tag>
                </Tooltip>
              )}
            </>
          ) : (
            <span style={{ color: '#999' }}>No permissions</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingRole(record);
              setErrorMessage('');
              form.setFieldsValue({
                name: record.name,
                description: record.description,
                permissions: record.permissions || []
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Are you sure to delete this role?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Data for demo
  const demoData = [
    {
      _id: '1',
      name: 'Admin',
      description: 'Full system administrator',
      permissions: ['users.manage', 'roles.manage', 'settings.edit'],
      isActive: true,
      formattedCreatedAt: '2023-06-18 00:55:39',
      formattedUpdatedAt: '2023-06-23 12:13:07'
    },
    {
      _id: '2',
      name: 'User',
      description: 'Regular user',
      permissions: ['profile.edit', 'dashboard.view'],
      isActive: true,
      formattedCreatedAt: '2023-05-15 10:30:25',
      formattedUpdatedAt: '2023-06-20 14:45:10'
    },
    {
      _id: '3',
      name: 'Manager',
      description: 'Regular manager',
      permissions: ['profile.edit', 'dashboard.view'],
      isActive: true,
      formattedCreatedAt: '2023-05-15 10:30:25',
      formattedUpdatedAt: '2023-06-20 14:45:10'
    },
    {
      _id: '4',
      name: 'Manager',
      description: 'Regular manager',
      permissions: ['profile.edit', 'dashboard.view'],
      isActive: true,
      formattedCreatedAt: '2023-05-15 10:30:25',
      formattedUpdatedAt: '2023-06-20 14:45:10'
    }
    
  ];

  // Use demo data if no real data
  const displayRoles = roles.length > 0 ? roles : demoData;
  const displayTotal = roles.length > 0 ? totalRoles : demoData.length;

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f0f2f5',
      minHeight: '100vh',
      borderRadius: '10px',

    }}>
      <Card style={{ borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', border: '1px solid #5fa5e7'}}>
        {/* Header avec le bouton Add New Role en haut à droite */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: '#333'
              }}>
                Gestion des Rôles
              </h1>
            </div>
            
            {/* Bouton Add New Role en haut à droite */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRole(null);
                setErrorMessage('');
                form.resetFields();
                setModalVisible(true);
              }}
              style={{
                backgroundColor: '#5fa5e7',
                borderColor: '#1890ff',
                padding: '8px 20px',
                height: 'auto',
                marginTop: '8px'
              }}
            >
               Ajouter Rôle
            </Button>
          </div>
        </div>

        {/* Entries info */}
        <div style={{
          marginBottom: '16px',
          padding: '8px 16px',
          backgroundColor: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          Show {pageSize} entries
        </div>

        {/* Search bar */}
        <div style={{ marginBottom: '20px' }}>
          <Row gutter={16} align="middle">
            <Col>
              <Input
                placeholder="Search for..."
                style={{ width: '200px' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{ backgroundColor: '#5fa5e7', borderColor: '#1890ff' }}
              >
                Rechercher avancée
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table Card */}
        <Card
          bordered={true}
          style={{
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            marginBottom: '20px',
            padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6'
                }}>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>ID</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Name</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Description</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Permissions</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Status</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRoles.map((role, index) => (
                  <tr key={role._id} style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                  }}>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>{index + 1}</td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057',
                      fontWeight: '500'
                    }}>{role.name}</td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>{role.description}</td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {role.permissions && role.permissions.slice(0, 2).map((perm, i) => (
                          <span key={i} style={{
                            backgroundColor: '#e6f7ff',
                            color: '#1890ff',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {perm}
                          </span>
                        ))}
                        {role.permissions && role.permissions.length > 2 && (
                          <span style={{
                            backgroundColor: '#fafafa',
                            color: '#666',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            border: '1px solid #d9d9d9'
                          }}>
                            +{role.permissions.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>
                      <span style={{
                        color: role.isActive ? '#52c41a' : '#ff4d4f',
                        fontWeight: '500'
                      }}>
                        {role.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#495057'
                    }}>
                      <Space>
                        <Button
                          type="primary"
                          onClick={() => {
                            setEditingRole(role);
                            setErrorMessage('');
                            form.setFieldsValue({
                              name: role.name,
                              description: role.description,
                              permissions: role.permissions || []
                            });
                            setModalVisible(true);
                          }}
                          style={{
                            backgroundColor: '#049472',
                            borderColor: '#057158',
                            fontSize: '12px',
                            padding: '4px 4px',
                            height: 'auto',
                            borderRadius: '50%'
                          }}
                        >
                          < EditOutlined />
                        </Button>
                        <Popconfirm
                          title="Are you sure to delete this role?"
                          onConfirm={() => handleDelete(role)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{
                            style: {
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545'
                            }
                          }}
                        >
                          <Button
                            danger
                            style={{
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545',
                              color: 'white',
                              fontSize: '12px',
                              padding: '4px 4px',
                              height: 'auto',
                              borderRadius: '50%'
                            }}
                          >
                            < DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      </Space>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination info and controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '12px 16px',
          backgroundColor: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: '4px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, displayTotal)} to {Math.min(currentPage * pageSize, displayTotal)} of {displayTotal} entries
          </div>
         
          <div>
            <Space>
              <Button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1, pageSize)}
                style={{ color: currentPage === 1 ? '#ccc' : '#1890ff' }}
              >
                Previous
              </Button>
             
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={displayTotal}
                onChange={handlePageChange}
                showSizeChanger={false}
                size="small"
                itemRender={(page, type, originalElement) => {
                  if (type === 'page') {
                    return (
                      <Button
                        type={page === currentPage ? 'primary' : 'default'}
                        size="small"
                        style={{
                          minWidth: '32px',
                          backgroundColor: page === currentPage ? '#1890ff' : 'transparent',
                          borderColor: page === currentPage ? '#1890ff' : '#d9d9d9'
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }
                  return originalElement;
                }}
              />
             
              <Button
                disabled={currentPage * pageSize >= displayTotal}
                onClick={() => handlePageChange(currentPage + 1, pageSize)}
                style={{ color: currentPage * pageSize >= displayTotal ? '#ccc' : '#1890ff' }}
              >
                Next
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Modal pour ajouter/modifier un rôle */}
      <Modal
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRole(null);
          form.resetFields();
          setErrorMessage('');
        }}
        footer={null}
        width={500}
        style={{ top: 20 }}
        bodyStyle={{ padding: '0' }}
      >
        {errorMessage && (
          <Alert
            message="Error"
            description={errorMessage}
            type="error"
            showIcon
            style={{
              margin: '20px 20px 0 20px',
              borderRadius: '4px'
            }}
            closable
            onClose={() => setErrorMessage('')}
          />
        )}
       
        <div style={{ padding: '20px' }}>
          <h1 style={{
            margin: '0 0 16px 0',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px'
          }}>
            {editingRole ? 'Edit Role' : 'Add New Role'}
          </h1>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '16px' }}
          >
            <div style={{ marginBottom: '24px' }}>
              <Form.Item
                name="name"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Role Name *
                  </span>
                }
                rules={[{ required: true, message: 'Please enter the role name' }]}
              >
                <Input
                  placeholder="Role name"
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    padding: '8px 12px'
                  }}
                />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Description *
                  </span>
                }
                rules={[{ required: true, message: 'Please enter the description' }]}
              >
                <TextArea
                  placeholder="Role description"
                  rows={3}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    padding: '8px 12px'
                  }}
                />
              </Form.Item>
            </div>

            <div style={{
              borderTop: '1px solid #dee2e6',
              margin: '24px 0',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '0 10px',
                color: '#6c757d',
                fontSize: '12px'
              }}>
                ● ● ●
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Form.Item
                name="permissions"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Permissions
                  </span>
                }
              >
                <Select
                  mode="multiple"
                  placeholder="Select permissions"
                  style={{ width: '100%' }}
                >
                  {permissions.map(perm => (
                    <Option key={perm._id} value={perm.name}>
                      <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                      {perm.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="isActive"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Status
                  </span>
                }
                initialValue={true}
              >
                <Select
                  placeholder="Select status"
                  style={{ width: '100%' }}
                >
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid #eee'
            }}>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingRole(null);
                  form.resetFields();
                  setErrorMessage('');
                }}
                disabled={submitting}
                style={{
                  border: '1px solid #6c757d',
                  color: '#6c757d',
                  borderRadius: '4px',
                  padding: '8px 24px',
                  fontWeight: '500',
                  backgroundColor: 'transparent',
                  minWidth: '100px'
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  borderRadius: '4px',
                  padding: '8px 24px',
                  fontWeight: '500',
                  minWidth: '100px'
                }}
              >
                {editingRole ? 'Update' : 'Validate'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      <style jsx>{`
        :global(.ant-table-thead > tr > th) {
          background-color: #fafafa;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #e8e8e8;
        }
       
        :global(.ant-table-tbody > tr > td) {
          border-bottom: 1px solid #e8e8e8;
        }
       
        :global(.ant-table-tbody > tr:hover > td) {
          background-color: #f5f5f5;
        }
       
        :global(.ant-card) {
          border: 1px solid #e8e8e8;
        }
       
        :global(.ant-btn-primary) {
          background-color: #1890ff;
          border-color: #1890ff;
        }
       
        :global(.ant-btn-primary:hover) {
          background-color: #40a9ff;
          border-color: #40a9ff;
        }
      `}</style>
    </div>
  );
};

export default RoleManagementPage;